import React from "react";
import { useTranslation } from "react-i18next";
import type { Recording } from "../../api/types";

const statusLabels = {
  completed: "activity.analyzed",
  analyzing: "analyzing_ellipsis",
  failed: "failed",
  cancelled: "cancelled",
  not_analyzed: "sessions_not_analyzed",
  not_synced: "activity.not_synced",
  recording_too_short: "recording_too_short",
} as const;

export function sessionStatus(recording: Recording) {
  return recording.rerun?.status === "running"
    ? "analyzing"
    : recording.rerun?.status === "failed"
      ? "failed"
      : recording.status.status;
}

// Completed sessions are re-run from their detail page, where run options are available.
export function canAnalyzeSession(recording: Recording) {
  return (
    recording.rerun?.status !== "running" &&
    ["not_analyzed", "failed", "cancelled"].includes(recording.status.status)
  );
}

export default function SessionStatus({ recording }: { recording: Recording }) {
  const { t } = useTranslation();
  const status = recording.status.status;
  const rerun = recording.rerun?.status;
  const statusLabel =
    rerun === "running"
      ? "rerunning_ellipsis"
      : rerun === "failed"
        ? "rerun_failed"
        : statusLabels[status];
  const tone =
    rerun === "failed" || status === "failed"
      ? "bg-rose-500/10 text-rose-700 dark:text-rose-400"
      : rerun === "running" || status === "analyzing"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : status === "completed"
          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : "bg-muted-surface text-text-mid";
  return (
    <span
      className={`inline-block max-w-full justify-self-start rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {t(statusLabel)}
    </span>
  );
}
